import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Modal,
  Fade,
  Backdrop,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  Badge,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Verified as VerifiedIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Key as KeyIcon,
  Security as SecurityIcon,
  SmartToy as BotIcon,
  Group as GroupIcon,
  Chat as ChatIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Assignment as ProjectIcon,
  People as TeamIcon,
  EditNote as CoAuthorIcon,
  Restore as RestoreIcon,
  Diversity3 as TeamCollabIcon, // New icon for Team Collaboration
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { connect, useSelector } from "react-redux";
import CryptoJS from "crypto-js";

// Services
import userService, { updatePhoneFromSettings } from "../services/userService";
import authService, { sendOtp, verifyOtp } from "../services/authService";
import adminServices from "../services/adminServices";
import uploadImageServices from "../services/uploadImageServices";
import { addUserLogs } from "../services/userLogServices";
import { setCurrentUser } from "../redux/user/user.actions";

// Components
import Loader from "../utils/loader";
import { TwoFactorComp } from "./ReusableComponents/TwoFactorComp";
import InputBoxMobile from "./ReusableComponents/InputBoxforMobile";
import OtpInputBox from "./ReusableComponents/OtpInputBox";

// Utils
import { countries } from "./ReusableComponents/countryList";

// Styles
import "react-toastify/dist/ReactToastify.css";
import "./settings.css";

// Bot Access Types - Updated to include TEAM_COLLAB
export const BOT_TYPES = {
  TEAM: "team",
  TEAM_COLLAB: "teamCollab", // New team collaboration type
  CHAT: "chat",
  COUTHOR: "couthor",
  PROJECT: "project",
};

// Bot Access Utility Functions - Updated
export const getBotAccess = (botType) => {
  try {
    const userIdentifier =
      sessionStorage.getItem("currentUserEmail") ||
      localStorage.getItem("currentUserEmail");
    if (!userIdentifier) return true;

    const savedPrefs = localStorage.getItem(`botAccess_${userIdentifier}`);
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      return prefs[botType] !== false;
    }
    return true;
  } catch (error) {
    console.error("Error getting bot access:", error);
    return true;
  }
};

export const getAllBotAccess = () => {
  try {
    const userIdentifier =
      sessionStorage.getItem("currentUserEmail") ||
      localStorage.getItem("currentUserEmail");
    if (!userIdentifier)
      return {
        team: true,
        teamCollab: true,
        chat: true,
        couthor: true,
        project: true,
      };

    const savedPrefs = localStorage.getItem(`botAccess_${userIdentifier}`);
    if (savedPrefs) {
      return JSON.parse(savedPrefs);
    }
    return {
      team: true,
      teamCollab: true,
      chat: true,
      couthor: true,
      project: true,
    };
  } catch (error) {
    console.error("Error getting all bot access:", error);
    return {
      team: true,
      teamCollab: true,
      chat: true,
      couthor: true,
      project: true,
    };
  }
};

const SettingsNew = ({
  setCurrentUser,
  manageUser = false,
  data: initialData = {},
}) => {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const userDetails = useSelector((state) => state.user);
  const currentUser = userDetails?.currentUser || {};

  // State
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [userCurrentData, setUserCurrentData] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Bot Access State - Updated to include teamCollab
  const [botAccess, setBotAccess] = useState({
    team: true, // Team bot (AI assistant for teams)
    teamCollab: true, // Team Collaboration feature (new)
    chat: true, // Chat screen bot
    couthor: true, // Co-author bot
    project: true, // Project management bot
  });

  // Edit states
  const [readonly, setReadonly] = useState({
    username: true,
    email: true,
    orgName: true,
    mobile: true,
  });

  // Password change
  const [passwordModal, setPasswordModal] = useState(false);
  const [npassword, setNPassword] = useState("");
  const [cpassword, setCPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    npassword: "",
    cpassword: "",
  });

  // Phone edit
  const [phoneModal, setPhoneModal] = useState(false);
  const [editedPhone, setEditedPhone] = useState({
    number: "",
    countryCode: "",
    countryName: "",
    fullNumber: "",
  });
  const [otpState, setOtpState] = useState({
    shown: false,
    value: "",
    verified: false,
    timer: 0,
    resendDisabled: true,
  });
  const [phoneError, setPhoneError] = useState("");

  // Two factor
  const [twoFactor, setTwoFactor] = useState({
    enabled: false,
    email: false,
    mobile: true,
    approved: false,
    requestMessage: "",
    requestSent: false,
  });

  // Profile image
  const fileInputRef = useRef(null);

  // Subscription
  const [subscription, setSubscription] = useState({
    plan: "",
    expiry: "",
    isExpired: false,
  });

  const countryLengthMap = {
    "+1": 10,
    "+91": 10,
    "+44": 10,
    "+61": 9,
    "+86": 11,
    "+49": 11,
    "+33": 9,
    "+81": 10,
    "+82": 10,
    "+971": 9,
    "+966": 9,
    "+974": 8,
    "+20": 10,
    "+27": 10,
    "+34": 9,
    "+39": 10,
    "+55": 11,
    "+52": 10,
    "+7": 10,
    "+90": 10,
    "+31": 9,
    "+46": 10,
    "+47": 8,
    "+45": 8,
    "+358": 9,
    "+353": 9,
    "+351": 9,
    "+30": 10,
    "+32": 9,
    "+41": 9,
    "+43": 13,
    "+48": 9,
    "+420": 9,
    "+36": 9,
    "+385": 9,
    "+386": 8,
    "+421": 9,
    "+380": 9,
    "+375": 9,
    "+359": 8,
    "+40": 10,
    "+7": 10,
  };

  const showToast = (message, type = "success") => {
    toast[type](message, {
      toastId: "bot-access-toast", // Same ID always
      position: "top-right",
      autoClose: 3000,
    });
  };

  useEffect(() => {
    fetchProfileData();
    checkTheme();
  }, []);

  useEffect(() => {
    if (data?.email) {
      fetchBotPreferences();
    }
  }, [data]);

  useEffect(() => {
    if (otpState.timer > 0) {
      const timer = setTimeout(() => {
        setOtpState((prev) => ({ ...prev, timer: prev.timer - 1 }));
      }, 1000);
      return () => clearTimeout(timer);
    } else if (otpState.timer === 0) {
      setOtpState((prev) => ({ ...prev, resendDisabled: false }));
    }
  }, [otpState.timer]);

  const checkTheme = () => {
    const theme = localStorage.getItem("ThemeSwitch");
    setIsDarkMode(theme === "true");
  };

  const fetchBotPreferences = async () => {
    try {
      // Get user identifier from data or currentUser
      const userIdentifier = data?.email || currentUser?.email;

      if (!userIdentifier) {
        console.warn("No user identifier found for bot preferences");
        return;
      }

      // Save email to storage for utility functions
      sessionStorage.setItem("currentUserEmail", userIdentifier);
      localStorage.setItem("currentUserEmail", userIdentifier);

      // Get bot preferences from localStorage
      const savedPrefs = localStorage.getItem(`botAccess_${userIdentifier}`);

      if (savedPrefs) {
        const parsedPrefs = JSON.parse(savedPrefs);
        setBotAccess(parsedPrefs);

        // Also store in sessionStorage as backup
        sessionStorage.setItem(`botAccess_${userIdentifier}`, savedPrefs);

        console.log("Loaded bot preferences:", parsedPrefs);
      } else {
        // Check sessionStorage as backup
        const sessionPrefs = sessionStorage.getItem(
          `botAccess_${userIdentifier}`,
        );
        if (sessionPrefs) {
          const parsedPrefs = JSON.parse(sessionPrefs);
          setBotAccess(parsedPrefs);
          localStorage.setItem(`botAccess_${userIdentifier}`, sessionPrefs);
        } else {
          // Set default values if no preferences exist
          const defaultPrefs = {
            team: true,
            teamCollab: true,
            chat: true,
            couthor: true,
            project: true,
          };
          setBotAccess(defaultPrefs);
          localStorage.setItem(
            `botAccess_${userIdentifier}`,
            JSON.stringify(defaultPrefs),
          );
          sessionStorage.setItem(
            `botAccess_${userIdentifier}`,
            JSON.stringify(defaultPrefs),
          );
        }
      }
    } catch (error) {
      console.error("Error fetching bot preferences:", error);
    }
  };

  const handleBotToggle = async (botType) => {
    const updatedAccess = {
      ...botAccess,
      [botType]: !botAccess[botType],
    };

    setBotAccess(updatedAccess);

    try {
      // Get user identifier
      const userIdentifier = data?.email || currentUser?.email;

      if (!userIdentifier) {
        toast.error("User identifier not found");
        return;
      }

      // Save to both localStorage and sessionStorage
      const prefsString = JSON.stringify(updatedAccess);
      localStorage.setItem(`botAccess_${userIdentifier}`, prefsString);
      sessionStorage.setItem(`botAccess_${userIdentifier}`, prefsString);

      showToast(
        `${getBotDisplayName(botType)} ${
          updatedAccess[botType] ? "activated" : "deactivated"
        }`,
      );

      // Log the action
      try {
        await addUserLogs({
          organizationId:
            currentUser?.organizationId ||
            sessionStorage.getItem("organizationId"),
          actionType: "updateBotAccess",
          botType: botType,
          status: updatedAccess[botType] ? "active" : "inactive",
          userId: currentUser?._id || sessionStorage.getItem("userId"),
        });
      } catch (logError) {
        console.warn("Could not log action:", logError);
      }

      // Dispatch custom event for other components
      window.dispatchEvent(
        new CustomEvent("botAccessChanged", {
          detail: {
            botType,
            status: updatedAccess[botType],
            allPrefs: updatedAccess,
          },
        }),
      );
    } catch (error) {
      console.error("Error updating bot access:", error);
      toast.error("Failed to update bot access");
      // Revert on error
      setBotAccess((prev) => ({
        ...prev,
        [botType]: !updatedAccess[botType],
      }));
    }
  };

  const getBotDisplayName = (botType) => {
    const names = {
      team: "Team Bot",
      teamCollab: "Team Collaboration", // New display name
      chat: "Chat Bot",
      couthor: "Co-author Bot",
      project: "Project Management Bot",
    };
    return names[botType] || botType;
  };

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const params = currentUser.email || initialData.email;
      const res = await userService.getUserProfile(params);
      const userData = res.data.user;
      const otp = res.data.OTP;

      setData(userData);
      setUserCurrentData(res.data.user);

      // Save email to storage for bot preferences
      if (userData?.email) {
        sessionStorage.setItem("currentUserEmail", userData.email);
        localStorage.setItem("currentUserEmail", userData.email);
      }

      // Set subscription
      if (
        userData.planID?.expireDate ||
        (userData.Role === "Individual" && userData?.plan?.endDate)
      ) {
        setSubscription({
          plan: userData.planName || "Free Trial",
          expiry: userData.planID?.expireDate || userData?.plan?.endDate,
          isExpired:
            new Date(userData.planID?.expireDate || userData?.plan?.endDate) <
            new Date(),
        });
      }

      // Set two factor
      setTwoFactor({
        enabled: userData.is2Factor || false,
        email: userData.is2FEmailChecked || false,
        mobile: true,
        approved: otp?.isApproved || false,
        requestMessage: otp?.message || "",
        requestSent: userData.isRequestSent || false,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile data");
      setLoading(false);
    }
  };

  const handleEdit = (field) => {
    setReadonly((prev) => ({ ...prev, [field]: false }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const userId = sessionStorage.getItem("userId");
      const changeEmail = userCurrentData?.email !== data?.email;

      const updatedData = {
        ...data,
        is2Factor: twoFactor.enabled,
        is2FMobileChecked: twoFactor.mobile,
        is2FEmailChecked: twoFactor.email,
        userId,
        changeEmail,
      };

      // Validation
      if (!updatedData.username || !updatedData.email || !updatedData.phone) {
        toast.warn("All fields are mandatory");
        setLoading(false);
        return;
      }

      if (!/^\S+@\S+\.\S+$/.test(updatedData.email)) {
        toast.warn("Please enter a valid email");
        setLoading(false);
        return;
      }

      const response = await userService.updateProfile(updatedData);

      if (response.status === 200) {
        toast.success(response.message);
        setCurrentUser(updatedData);
        setReadonly({
          username: true,
          email: true,
          orgName: true,
          mobile: true,
        });

        // Update session
        sessionStorage.setItem("organizationId", response?.organizationId);
        sessionStorage.setItem("currentUserEmail", response?.user?.email);

        // Update email in storage for bot preferences
        if (response?.user?.email) {
          localStorage.setItem("currentUserEmail", response.user.email);
        }

        // Log action
        await addUserLogs({
          organizationId: currentUser.organizationId,
          actionType: "updateProfile",
          userId: currentUser._id,
        });

        if (response?.isEmailChanged) {
          history.push("/logout");
        }
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);

    try {
      const uploadConfig = await uploadImageServices.UploadImageToS3(
        file,
        data?.key,
      );
      const updatedUser = {
        ...data,
        display_picture: uploadConfig.data.displayUrl,
        key: uploadConfig.data.key,
      };

      setData(updatedUser);
      setCurrentUser(updatedUser);
      await userService.updateProfile(updatedUser);
      toast.success("Profile picture updated");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    // Validation
    if (npassword.length < 8) {
      setPasswordErrors((prev) => ({
        ...prev,
        npassword: "Password must be at least 8 characters",
      }));
      return;
    }

    if (!/[A-Z]/.test(npassword)) {
      setPasswordErrors((prev) => ({
        ...prev,
        npassword: "Password must contain at least one uppercase letter",
      }));
      return;
    }

    if (!/[0-9]/.test(npassword)) {
      setPasswordErrors((prev) => ({
        ...prev,
        npassword: "Password must contain at least one number",
      }));
      return;
    }

    if (npassword !== cpassword) {
      setPasswordErrors((prev) => ({
        ...prev,
        cpassword: "Passwords do not match",
      }));
      return;
    }

    setLoading(true);

    try {
      const hashedPassword = CryptoJS.SHA256(npassword).toString();
      const result = await authService.resetPassword({
        email: data.email,
        password: hashedPassword,
        cpassword: hashedPassword,
      });

      if (result.status === "good") {
        toast.success(result.message);
        setPasswordModal(false);
        setNPassword("");
        setCPassword("");
        setPasswordErrors({ npassword: "", cpassword: "" });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    Swal.fire({
      title: "Are you sure?",
      text:
        currentUser.Role === "Organization"
          ? "Your request will be sent to Admin for approval"
          : "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);

        try {
          if (currentUser.Role === "Organization") {
            await adminServices.updateUserRequest({
              email: currentUser.email,
              typeOfRequest: "Delete",
            });

            await addUserLogs({
              organizationId: currentUser.organizationId,
              actionType: "userRequest",
              typeOfRequest: "Request Delete Account",
              userId: currentUser._id,
            });

            toast.info("Delete request has been sent to Organization Admin");
          } else {
            const response = await userService.deleteAccount();
            if (response.status === 200) {
              toast.success(response.message);
              history.push("/");
            }
          }
        } catch (error) {
          console.error("Error deleting account:", error);
          toast.error("Failed to delete account");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handlePhoneEdit = () => {
    const fullPhone = data?.phone || "";
    let countryCode = "";
    let phoneNumber = "";

    // Find matching country code
    for (const code in countryLengthMap) {
      if (fullPhone.startsWith(code)) {
        countryCode = code;
        phoneNumber = fullPhone.slice(code.length);
        break;
      }
    }

    // If no match found, use default
    if (!countryCode) {
      countryCode = "+1";
      phoneNumber = fullPhone;
    }

    const country = countries.find(
      (c) => c.phone === countryCode.replace("+", ""),
    );

    setEditedPhone({
      number: phoneNumber,
      countryCode,
      countryName: country?.label || "United States",
      fullNumber: fullPhone,
    });

    setPhoneModal(true);
  };

  const handleVerifyOTP = async () => {
    if (!editedPhone.fullNumber) {
      toast.warn("Please provide phone number");
      return;
    }

    if (editedPhone.fullNumber === data.phone) {
      toast.warn("This is your current number. Enter a new number to update.");
      return;
    }

    setLoading(true);

    try {
      const response = await sendOtp({ phone: editedPhone.fullNumber });

      if (response.status === 200) {
        toast.success("OTP sent successfully");
        setOtpState((prev) => ({
          ...prev,
          shown: true,
          timer: 60,
          resendDisabled: true,
        }));
      } else {
        toast.error("Invalid phone number");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = async (otp) => {
    setOtpState((prev) => ({ ...prev, value: otp }));

    if (otp.length === 6) {
      setLoading(true);

      try {
        const response = await verifyOtp({
          phone: editedPhone.fullNumber,
          otp,
        });

        if (response.status === 200) {
          setOtpState((prev) => ({
            ...prev,
            verified: true,
            shown: false,
          }));
          toast.success("Phone number verified");
        } else {
          toast.error("Invalid OTP");
        }
      } catch (error) {
        console.error("Error verifying OTP:", error);
        toast.error("Failed to verify OTP");
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePhoneSave = async () => {
    if (!otpState.verified) {
      toast.warn("Please verify your phone number first");
      return;
    }

    setLoading(true);

    try {
      const userId = sessionStorage.getItem("userId");
      const response = await updatePhoneFromSettings({
        phone: editedPhone.fullNumber,
        userId,
      });

      if (response.status === 200) {
        toast.success(response.message);
        await fetchProfileData();
        setPhoneModal(false);
        setOtpState({
          shown: false,
          value: "",
          verified: false,
          timer: 0,
          resendDisabled: true,
        });
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error updating phone:", error);
      toast.error("Failed to update phone number");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Bot Access Section Component - Updated with Team Collaboration
  const BotAccessSection = () => (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <BotIcon color="primary" />
        Feature Access Control
      </Typography>
      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Toggle features on/off for different capabilities. When inactive, the
        feature won't be available in the application. Your preferences are
        saved and will persist even after closing the application.
      </Typography>

      <Grid container spacing={2}>
        {/* Team Collaboration Feature (New) */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: botAccess.teamCollab
                ? "success.lighter"
                : "action.hover",
              borderColor: botAccess.teamCollab ? "success.main" : "divider",
              transition: "all 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 2,
                borderColor: botAccess.teamCollab
                  ? "success.dark"
                  : "primary.main",
              },
            }}
            onClick={() => handleBotToggle("teamCollab")}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TeamCollabIcon
                  color={botAccess.teamCollab ? "success" : "disabled"}
                />
                <Typography variant="subtitle1" fontWeight="600">
                  Team Collaboration
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {botAccess.teamCollab ? (
                  <ToggleOnIcon sx={{ fontSize: 40, color: "success.main" }} />
                ) : (
                  <ToggleOffIcon
                    sx={{ fontSize: 40, color: "action.disabled" }}
                  />
                )}
              </Box>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Controls team collaboration features including team spaces, member
              management, and team-based permissions
            </Typography>

            <Chip
              label={botAccess.teamCollab ? "Active" : "Inactive"}
              color={botAccess.teamCollab ? "success" : "default"}
              size="small"
              sx={{
                height: 24,
                "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
              }}
            />
          </Paper>
        </Grid>

        {/* Team Bot (AI Assistant) */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: botAccess.team ? "info.lighter" : "action.hover",
              borderColor: botAccess.team ? "info.main" : "divider",
              transition: "all 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 2,
                borderColor: botAccess.team ? "info.dark" : "primary.main",
              },
            }}
            onClick={() => handleBotToggle("team")}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TeamIcon color={botAccess.team ? "info" : "disabled"} />
                <Typography variant="subtitle1" fontWeight="600">
                  Team Bot (AI Assistant)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {botAccess.team ? (
                  <ToggleOnIcon sx={{ fontSize: 40, color: "info.main" }} />
                ) : (
                  <ToggleOffIcon
                    sx={{ fontSize: 40, color: "action.disabled" }}
                  />
                )}
              </Box>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Controls AI bot assistance in team collaboration, member invites,
              and team chat suggestions
            </Typography>

            <Chip
              label={botAccess.team ? "Active" : "Inactive"}
              color={botAccess.team ? "info" : "default"}
              size="small"
              sx={{
                height: 24,
                "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
              }}
            />
          </Paper>
        </Grid>

        {/* Co-author Bot */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: botAccess.couthor ? "warning.lighter" : "action.hover",
              borderColor: botAccess.couthor ? "warning.main" : "divider",
              transition: "all 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 2,
                borderColor: botAccess.couthor
                  ? "warning.dark"
                  : "primary.main",
              },
            }}
            onClick={() => handleBotToggle("couthor")}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CoAuthorIcon
                  color={botAccess.couthor ? "warning" : "disabled"}
                />
                <Typography variant="subtitle1" fontWeight="600">
                  Co-author Bot
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {botAccess.couthor ? (
                  <ToggleOnIcon sx={{ fontSize: 40, color: "warning.main" }} />
                ) : (
                  <ToggleOffIcon
                    sx={{ fontSize: 40, color: "action.disabled" }}
                  />
                )}
              </Box>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Controls bot in document collaboration, comments, and co-authoring
              features
            </Typography>

            <Chip
              label={botAccess.couthor ? "Active" : "Inactive"}
              color={botAccess.couthor ? "warning" : "default"}
              size="small"
              sx={{
                height: 24,
                "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
              }}
            />
          </Paper>
        </Grid>

        {/* Project Management Bot */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: botAccess.project ? "secondary.lighter" : "action.hover",
              borderColor: botAccess.project ? "secondary.main" : "divider",
              transition: "all 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 2,
                borderColor: botAccess.project
                  ? "secondary.dark"
                  : "primary.main",
              },
            }}
            onClick={() => handleBotToggle("project")}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ProjectIcon
                  color={botAccess.project ? "secondary" : "disabled"}
                />
                <Typography variant="subtitle1" fontWeight="600">
                  Co-Auth Project Bot
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {botAccess.project ? (
                  <ToggleOnIcon
                    sx={{ fontSize: 40, color: "secondary.main" }}
                  />
                ) : (
                  <ToggleOffIcon
                    sx={{ fontSize: 40, color: "action.disabled" }}
                  />
                )}
              </Box>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Controls bot in project creation, task management, and project
              collaboration
            </Typography>

            <Chip
              label={botAccess.project ? "Active" : "Inactive"}
              color={botAccess.project ? "secondary" : "default"}
              size="small"
              sx={{
                height: 24,
                "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
              }}
            />
          </Paper>
        </Grid>

        {/* Chat Bot */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: botAccess.chat ? "primary.lighter" : "action.hover",
              borderColor: botAccess.chat ? "primary.main" : "divider",
              transition: "all 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 2,
                borderColor: botAccess.chat ? "primary.dark" : "primary.main",
              },
            }}
            onClick={() => handleBotToggle("chat")}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ChatIcon color={botAccess.chat ? "primary" : "disabled"} />
                <Typography variant="subtitle1" fontWeight="600">
                  Chat Bot
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {botAccess.chat ? (
                  <ToggleOnIcon sx={{ fontSize: 40, color: "primary.main" }} />
                ) : (
                  <ToggleOffIcon
                    sx={{ fontSize: 40, color: "action.disabled" }}
                  />
                )}
              </Box>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Controls bot in general chat, AI assistance, and messaging
              features
            </Typography>

            <Chip
              label={botAccess.chat ? "Active" : "Inactive"}
              color={botAccess.chat ? "primary" : "default"}
              size="small"
              sx={{
                height: 24,
                "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Status Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: "background.default", borderRadius: 2 }}>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <VerifiedIcon fontSize="small" color="success" />
          Current Feature Status
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
        >
          {botAccess.teamCollab && (
            <Chip
              icon={<TeamCollabIcon />}
              label="Team Collaboration: Active"
              color="success"
              size="small"
              variant="outlined"
            />
          )}
          {botAccess.team && (
            <Chip
              icon={<TeamIcon />}
              label="Team Bot: Active"
              color="info"
              size="small"
              variant="outlined"
            />
          )}
          {botAccess.couthor && (
            <Chip
              icon={<CoAuthorIcon />}
              label="Co-author Bot: Active"
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          {botAccess.project && (
            <Chip
              icon={<ProjectIcon />}
              label="Project Bot: Active"
              color="secondary"
              size="small"
              variant="outlined"
            />
          )}
          {botAccess.chat && (
            <Chip
              icon={<ChatIcon />}
              label="Chat Bot: Active"
              color="primary"
              size="small"
              variant="outlined"
            />
          )}
          {!botAccess.teamCollab &&
            !botAccess.team &&
            !botAccess.couthor &&
            !botAccess.project &&
            !botAccess.chat && (
              <Typography variant="body2" color="text.secondary">
                All features are currently inactive
              </Typography>
            )}
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          Your feature preferences are saved and will persist when you close and
          reopen the application.
        </Alert>
      </Box>

      {/* Quick Actions - Updated */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<ToggleOnIcon />}
          onClick={() => {
            const allActive = {
              team: true,
              teamCollab: true,
              chat: true,
              couthor: true,
              project: true,
            };
            setBotAccess(allActive);
            const userIdentifier = data?.email || currentUser?.email;
            if (userIdentifier) {
              localStorage.setItem(
                `botAccess_${userIdentifier}`,
                JSON.stringify(allActive),
              );
              sessionStorage.setItem(
                `botAccess_${userIdentifier}`,
                JSON.stringify(allActive),
              );
            }

            showToast("All features activated");
          }}
        >
          Activate All
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          startIcon={<RestoreIcon />}
          onClick={() => {
            const defaultConfig = {
              team: true,
              teamCollab: true,
              chat: true,
              couthor: true,
              project: true,
            };
            setBotAccess(defaultConfig);
            const userIdentifier = data?.email || currentUser?.email;
            if (userIdentifier) {
              localStorage.setItem(
                `botAccess_${userIdentifier}`,
                JSON.stringify(defaultConfig),
              );
              sessionStorage.setItem(
                `botAccess_${userIdentifier}`,
                JSON.stringify(defaultConfig),
              );
            }
            showToast("Reset to default configuration");
          }}
        >
          Reset to Default
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<ToggleOffIcon />}
          onClick={() => {
            const allInactive = {
              team: false,
              teamCollab: false,
              chat: false,
              couthor: false,
              project: false,
            };
            setBotAccess(allInactive);
            const userIdentifier = data?.email || currentUser?.email;
            if (userIdentifier) {
              localStorage.setItem(
                `botAccess_${userIdentifier}`,
                JSON.stringify(allInactive),
              );
              sessionStorage.setItem(
                `botAccess_${userIdentifier}`,
                JSON.stringify(allInactive),
              );
            }
            showToast("All features deactivated");
          }}
        >
          Deactivate All
        </Button>
      </Box>
    </Paper>
  );

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Loader loading={loading} />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        limit={1}
        newestOnTop
      />

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Profile Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: isDarkMode
              ? "rgba(255,255,255,0.05)"
              : "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm="auto" sx={{ textAlign: "center" }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "primary.main",
                      "&:hover": { bgcolor: "primary.dark" },
                      color: "white",
                    }}
                    onClick={() => fileInputRef.current.click()}
                  >
                    <CloudUploadIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Avatar
                  src={data?.display_picture}
                  sx={{
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    border: "3px solid",
                    borderColor: "primary.main",
                    mx: { xs: "auto", sm: 0 },
                  }}
                />
              </Badge>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
            </Grid>

            <Grid item xs={12} sm>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    flexWrap: "wrap",
                    justifyContent: { xs: "center", sm: "flex-start" },
                  }}
                >
                  {readonly.username ? (
                    <>
                      <Typography variant="h5" fontWeight="600">
                        {data?.username}
                      </Typography>
                      <Tooltip title="Edit username">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit("username")}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <TextField
                      name="username"
                      value={data?.username || ""}
                      onChange={handleChange}
                      size="small"
                      autoFocus
                      onBlur={() =>
                        setReadonly((prev) => ({ ...prev, username: true }))
                      }
                      sx={{ maxWidth: "300px" }}
                    />
                  )}

                  <Chip
                    label={
                      currentUser.isAdmin
                        ? "Admin"
                        : currentUser.isSubAdmin
                          ? "Sub Admin"
                          : "User"
                    }
                    color={
                      currentUser.isAdmin
                        ? "secondary"
                        : currentUser.isSubAdmin
                          ? "warning"
                          : "info"
                    }
                    size="small"
                  />
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon color="action" fontSize="small" />
                    {readonly.email ? (
                      <>
                        <Typography variant="body2">{data?.email}</Typography>
                        <Tooltip title="Edit email">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit("email")}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <TextField
                        name="email"
                        value={data?.email || ""}
                        onChange={handleChange}
                        size="small"
                        type="email"
                        autoFocus
                        onBlur={() =>
                          setReadonly((prev) => ({ ...prev, email: true }))
                        }
                      />
                    )}
                    {userCurrentData?.email === data?.email && (
                      <VerifiedIcon color="success" fontSize="small" />
                    )}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {data?.phone || "Not provided"}
                    </Typography>
                    <Tooltip title="Update phone number">
                      <IconButton size="small" onClick={handlePhoneEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sm="auto"
              sx={{ textAlign: { xs: "center", sm: "right" } }}
            >
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{ mb: 1 }}
              >
                Save Changes
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Left Column - Organization & Subscription */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {data?.Role === "Organization" && (
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <BusinessIcon color="primary" />
                    Organization Details
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Organization ID
                      </Typography>
                      <Typography variant="body2">
                        {data?.organizationId}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Organization Name
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {readonly.orgName ? (
                          <>
                            <Typography variant="body2">
                              {data?.organizationName}
                            </Typography>
                            <Tooltip title="Edit organization name">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit("orgName")}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <TextField
                            name="organizationName"
                            value={data?.organizationName || ""}
                            onChange={handleChange}
                            size="small"
                            fullWidth
                            autoFocus
                            onBlur={() =>
                              setReadonly((prev) => ({
                                ...prev,
                                orgName: true,
                              }))
                            }
                          />
                        )}
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              )}

              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <KeyIcon color="primary" />
                  Subscription Plan
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography
                    variant="h5"
                    color="primary"
                    fontWeight="600"
                    gutterBottom
                  >
                    {subscription.plan}
                  </Typography>

                  {subscription.expiry && (
                    <Chip
                      label={`Expires: ${new Date(subscription.expiry).toLocaleDateString()}`}
                      color={subscription.isExpired ? "error" : "success"}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  component="a"
                  href="/pricing"
                  sx={{ mt: 2 }}
                >
                  Upgrade Plan
                </Button>
              </Paper>
            </Stack>
          </Grid>

          {/* Right Column - Security & Bot Access */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* Security Settings */}
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <SecurityIcon color="primary" />
                  Security Settings
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<LockIcon />}
                      onClick={() => setPasswordModal(true)}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Change Password
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteAccount}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      {currentUser.Role === "Organization"
                        ? "Request Delete"
                        : "Delete Account"}
                    </Button>
                  </Grid>

                  {(currentUser.Role === "Organization" ||
                    currentUser.isSubAdmin) && (
                    <Grid item xs={12}>
                      <TwoFactorComp
                        data={{
                          is2Factor: twoFactor.enabled,
                          is2FEmailChecked: twoFactor.email,
                          is2FactorApproved: twoFactor.approved,
                          is2FReqApprovedMsg: twoFactor.requestMessage,
                          isRequestSent: twoFactor.requestSent,
                          email: data?.email,
                          isSubAdmin: currentUser.isSubAdmin,
                        }}
                        getProfile={fetchProfileData}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Feature Access Control Section */}
              <BotAccessSection />
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Password Change Modal */}
      <Modal
        open={passwordModal}
        onClose={() => setPasswordModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={passwordModal}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 400 },
              p: 4,
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Stack spacing={3}>
              <TextField
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={npassword}
                onChange={(e) => setNPassword(e.target.value)}
                error={!!passwordErrors.npassword}
                helperText={passwordErrors.npassword}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm New Password"
                type="password"
                value={cpassword}
                onChange={(e) => setCPassword(e.target.value)}
                error={!!passwordErrors.cpassword}
                helperText={passwordErrors.cpassword}
                fullWidth
                disabled={!npassword}
              />

              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={() => setPasswordModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handlePasswordChange}
                  disabled={
                    !npassword ||
                    !cpassword ||
                    !!passwordErrors.npassword ||
                    !!passwordErrors.cpassword
                  }
                >
                  Update Password
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Fade>
      </Modal>

      {/* Phone Edit Modal */}
      <Modal
        open={phoneModal}
        onClose={() => setPhoneModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={phoneModal}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 450 },
              p: 4,
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Update Phone Number
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" gutterBottom>
                  Current Number: {data?.phone || "Not set"}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <InputBoxMobile
                    onChange={(value) => {
                      const maxLength =
                        countryLengthMap[editedPhone.countryCode] || 10;
                      if (value.length <= maxLength) {
                        setEditedPhone((prev) => ({
                          ...prev,
                          number: value,
                          fullNumber: `${prev.countryCode}${value}`,
                        }));

                        if (value.length === maxLength && value.length > 0) {
                          setPhoneError("");
                        } else if (value.length > 0) {
                          setPhoneError(
                            `Please enter a ${maxLength} digit number`,
                          );
                        } else {
                          setPhoneError("");
                        }
                      }
                    }}
                    handleCountryCode={(event, value) => {
                      const newCountryCode = `+${value.phone}`;
                      setEditedPhone({
                        number: "",
                        countryCode: newCountryCode,
                        countryName: value.label,
                        fullNumber: `+${value.phone}`,
                      });
                      setPhoneError("");
                      setOtpState({
                        shown: false,
                        value: "",
                        verified: false,
                        timer: 0,
                        resendDisabled: true,
                      });
                    }}
                    value={editedPhone.number}
                    countryCode={editedPhone.countryCode}
                    selectedCountryCode={editedPhone.countryName}
                  />
                  {phoneError && (
                    <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                      {phoneError}
                    </Alert>
                  )}
                </Box>
              </Box>

              {otpState.shown && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <OtpInputBox
                      value={otpState.value}
                      onChange={handleOTPChange}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={handleVerifyOTP}
                      disabled={otpState.resendDisabled}
                    >
                      Resend Code
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {formatTime(otpState.timer)}
                    </Typography>
                  </Box>
                </>
              )}

              {otpState.verified && (
                <Alert icon={<CheckCircleIcon />} severity="success">
                  Phone number verified successfully
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button variant="outlined" onClick={() => setPhoneModal(false)}>
                  Cancel
                </Button>

                {!otpState.verified && (
                  <Button
                    variant="contained"
                    onClick={handleVerifyOTP}
                    disabled={
                      !editedPhone.fullNumber ||
                      editedPhone.fullNumber === data?.phone ||
                      !!phoneError
                    }
                  >
                    Send OTP
                  </Button>
                )}

                {otpState.verified && (
                  <Button variant="contained" onClick={handlePhoneSave}>
                    Save Changes
                  </Button>
                )}
              </Box>
            </Stack>
          </Paper>
        </Fade>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.user.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentUser: (user) => dispatch(setCurrentUser(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsNew);
